import { useMemo, useState } from "react";
import { useAuthState } from "../../../../MainComponents/GlobalContext";
import HelmetTags from "../../../../MainComponents/HelmetTags";
import useTable from "../../../../MainComponents/Hooks/useTable";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRightFromBracket,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import useAxios from "../../../../MainComponents/Hooks/useAxios";
import { Popconfirm } from "antd";

function Students() {
  const AuthState = useAuthState();
  const [academiclevel, setAcademiclevel] = useState("1");
  const [left, setLeft] = useState("left-0");
  const [academicType, setAcademicType] = useState("scientific");
  const [systemType, setSystemType] = useState("hand_by_hand");
  const [reFetch, setReFetch] = useState(false);
  const [submitLogout, setSubmitLogout] = useState({
    flag: "",
    dependency: false,
    data: {},
  });

  const [getAllStudent, , getAllStudentloading] = useAxios(
    `${process.env.REACT_APP_ADMIN_STUDENT_TAB_API}${academiclevel}`,
    "GET",
    academiclevel,
    reFetch || academiclevel,
  );

  const filteredData = useMemo(() => {
    const list = Array.isArray(getAllStudent?.data) ? getAllStudent.data : [];
    let filtered = list;

    // Section + system filters only for 3rd sec
    if (academiclevel === "3") {
      filtered = list.filter((student) => {
        if (student.section !== academicType) return false;
        if (academicType === "scientific") {
          return student.system_type === systemType;
        }
        return true;
      });
    }

    // Re-number sequentially for the current filtered list
    return filtered.map((student, index) => ({
      ...student,
      n: index + 1,
    }));
  }, [getAllStudent?.data, academiclevel, academicType, systemType]);

  const filterCounts = useMemo(() => {
    const list = Array.isArray(getAllStudent?.data) ? getAllStudent.data : [];
    if (academiclevel !== "3") {
      return { total: list.length };
    }

    const scientific = list.filter((s) => s.section === "scientific");
    const literary = list.filter((s) => s.section === "literary");

    return {
      scientific: scientific.length,
      literary: literary.length,
      hand_by_hand: scientific.filter((s) => s.system_type === "hand_by_hand")
        .length,
      normal: scientific.filter((s) => s.system_type === "normal").length,
      current: filteredData.length,
    };
  }, [getAllStudent?.data, academiclevel, filteredData.length]);

  const [, , logoutloading] = useAxios(
    process.env.REACT_APP_LOGOUT_ALL_STUDENT_API,
    "POST",
    submitLogout.flag,
    submitLogout.dependency,
    submitLogout.data,
    true,
  );

  const table = useTable(
    "students",
    "",
    "",
    filteredData,
    `${academicType}-${systemType}`,
    academiclevel,
    true,
    filteredData,
    reFetch,
    setReFetch,
  );

  const handleLogout = () => {
    setSubmitLogout({
      flag: "LogoutAllStudents",
      dependency: !submitLogout.dependency,
      data: {},
    });
  };

  return (
    <section className="dark:bg-dark dark:text-light flex h-full w-full flex-col items-start overflow-y-auto px-10 py-10 sm:px-4 md:items-center">
      <HelmetTags title="Students | Mr.Mohamed Salama" />
      <div className="mb-7 flex w-full flex-wrap items-center justify-between gap-5">
        <h2 className="text-start text-2xl font-bold text-slate-900 dark:text-light">
          Students
        </h2>
        <div className="flex flex-wrap gap-3">
          {(AuthState.userData?.admin?.roles[0]?.name === "superadmin" ||
            AuthState.userData?.admin?.permissions?.some((permission) =>
              permission.name.startsWith("request_accept"),
            )) && (
            <Popconfirm
              title="Are you sure you want to logout all students ?"
              onConfirm={handleLogout}
              okText="Confirm"
              cancelText="Cancel"
              okButtonProps={{ loading: logoutloading }}
              okType="danger"
            >
              <button
                disabled={logoutloading}
                className="rounded-lg border-2 border-delete bg-delete px-3 py-1.5 font-semibold text-white transition-all duration-300 hover:brightness-110 active:scale-90 dark:border-red-400 dark:bg-red-500/90"
              >
                Logout All Students{" "}
                <FontAwesomeIcon
                  className="ml-2"
                  icon={faArrowRightFromBracket}
                />
              </button>
            </Popconfirm>
          )}
          {(AuthState.userData?.admin?.roles[0]?.name === "superadmin" ||
            AuthState.userData?.admin?.permissions?.some((permission) =>
              permission.name.startsWith("request_accept"),
            )) && (
            <Link
              to="/admin/students/deleted"
              className="rounded-lg border-2 border-red-400 bg-transparent px-3 py-1.5 font-semibold text-red-500 transition-all duration-300 hover:bg-red-500 hover:text-white active:scale-90 dark:border-red-400 dark:text-red-300 dark:hover:bg-red-500 dark:hover:text-white"
            >
              Deleted Students{" "}
              <FontAwesomeIcon className="ml-2" icon={faTrash} />
            </Link>
          )}
        </div>
      </div>

      <div className="relative flex h-10 min-h-[40px] w-72 justify-start overflow-hidden rounded-lg border border-slate-200 bg-slate-100 dark:border-slate-600 dark:bg-[#121a35]">
        <button
          type="button"
          onClick={() => {
            setLeft("left-0");
            setAcademiclevel("1");
          }}
          className="z-10 h-full w-1/3 text-base font-semibold text-slate-800 dark:text-slate-100"
        >
          1st sec
        </button>
        <button
          type="button"
          onClick={() => {
            setLeft("left-1/3");
            setAcademiclevel("2");
          }}
          className="z-10 h-full w-1/3 text-base font-semibold text-slate-800 dark:text-slate-100"
        >
          2nd sec
        </button>
        <button
          type="button"
          onClick={() => {
            setLeft("left-2/3");
            setAcademiclevel("3");
          }}
          className="z-10 h-full w-1/3 text-base font-semibold text-slate-800 dark:text-slate-100"
        >
          3rd sec
        </button>
        <div
          className={`std-selector absolute top-0 h-full w-1/3 ${left} rounded-md bg-green duration-300`}
        ></div>
      </div>

      {/* Filters for 3rd sec: Scientific/Literary + ايد بايد/النظام العادي */}
      {academiclevel === "3" ? (
        <div className="mt-8 flex w-full flex-col items-center gap-4">
          <div className="filters-container flex w-full justify-center bg-slate-100 dark:bg-[#121a35]">
            <div className="filters relative flex h-12 w-[800px] items-center justify-between transition-all duration-200 ease-in-out amd-hero:w-28 amd-hero:flex-col">
              <div
                onClick={() => setAcademicType("scientific")}
                className={`calculus flex h-full w-1/2 cursor-pointer items-center justify-center gap-2 font-semibold amd-hero:h-[calc(450px/9)] amd-hero:w-28 ${
                  academicType === "scientific" ? "bg-green" : ""
                }`}
              >
                Scientific
                <span className="rounded-full bg-black/15 px-2 py-0.5 text-xs font-bold">
                  {filterCounts.scientific ?? 0}
                </span>
              </div>
              <div
                onClick={() => setAcademicType("literary")}
                className={`Algebra flex h-full w-1/2 cursor-pointer items-center justify-center gap-2 font-semibold amd-hero:h-[calc(450px/9)] amd-hero:w-28 ${
                  academicType === "literary" ? "bg-green" : ""
                }`}
              >
                Literary
                <span className="rounded-full bg-black/15 px-2 py-0.5 text-xs font-bold">
                  {filterCounts.literary ?? 0}
                </span>
              </div>
            </div>
          </div>

          {academicType === "scientific" && (
            <div className="filters-container flex w-full justify-center bg-slate-100 dark:bg-[#121a35]">
              <div className="filters relative flex h-12 w-[800px] items-center justify-between transition-all duration-200 ease-in-out amd-hero:w-28 amd-hero:flex-col">
                <div
                  onClick={() => setSystemType("hand_by_hand")}
                  className={`flex h-full w-1/2 cursor-pointer items-center justify-center gap-2 font-semibold amd-hero:h-[calc(450px/9)] amd-hero:w-28 ${
                    systemType === "hand_by_hand" ? "bg-green" : ""
                  }`}
                >
                  ايد بايد
                  <span className="rounded-full bg-black/15 px-2 py-0.5 text-xs font-bold">
                    {filterCounts.hand_by_hand ?? 0}
                  </span>
                </div>
                <div
                  onClick={() => setSystemType("normal")}
                  className={`flex h-full w-1/2 cursor-pointer items-center justify-center gap-2 font-semibold amd-hero:h-[calc(450px/9)] amd-hero:w-28 ${
                    systemType === "normal" ? "bg-green" : ""
                  }`}
                >
                  النظام العادي
                  <span className="rounded-full bg-black/15 px-2 py-0.5 text-xs font-bold">
                    {filterCounts.normal ?? 0}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : null}

      <div className="mt-4 flex w-full items-center justify-between gap-3">
        <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">
          عدد الطلبة:{" "}
          <span className="text-base font-extrabold text-secondary dark:text-[#7eb6ff]">
            {academiclevel === "3"
              ? filterCounts.current ?? 0
              : filterCounts.total ?? 0}
          </span>
        </p>
      </div>

      <div className="relative mt-2 h-fit w-full">
        {getAllStudentloading && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center bg-white/40 pt-24 dark:bg-dark/40">
            <span className="rounded-lg bg-secondary px-4 py-2 text-sm font-semibold text-white">
              Loading students...
            </span>
          </div>
        )}
        {table}
      </div>
    </section>
  );
}

export default Students;
